import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faSpinner, faTrash, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { routes } from '../../config/utility';

const AccountDeletionConfirmation = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('');
    const [requestId, setRequestId] = useState('');

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setStatus('error');
            setMessage('Invalid confirmation link');
        }
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await api.get(`/account/verify-deletion/${token}`);

            if (response) {
                setStatus('success');
                setMessage(response.message);
                setRequestId(response.data?.request_id || '');
                toast.success('Account deletion confirmed successfully!');
            } else {
                setStatus('error');
                setMessage(response.data.message || 'Invalid or expired confirmation link');
            }
        } catch (error) {
            console.error('Verification error:', error);
            setStatus('error');
            setMessage(error.response?.data?.message || 'Failed to verify deletion confirmation');
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'verifying':
                return (
                    <div className="text-center">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="bg-blue-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6"
                        >
                            <FontAwesomeIcon icon={faSpinner} className="text-blue-500 text-2xl sm:text-3xl" />
                        </motion.div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-dark mb-2 sm:mb-3">Verifying Request</h1>
                        <p className="text-body text-sm sm:text-base lg:text-lg mb-3 sm:mb-4">Please wait while we verify your deletion request...</p>

                        {/* Token Display for Debug */}
                        <div className="bg-gray-100 rounded-lg sm:rounded-xl p-2 sm:p-3 mt-3 sm:mt-4">
                            <p className="text-xs text-gray-600 mb-1">Verification Token:</p>
                            <p className="text-xs font-mono text-gray-800 break-all">
                                {token?.substring(0, 20)}...
                            </p>
                        </div>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 10 }}
                            className="bg-green-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-2xl sm:text-3xl" />
                        </motion.div>

                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600 mb-2 sm:mb-3">Request Confirmed!</h1>
                        <p className="text-body text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">{message}</p>

                        {/* Request ID Display */}
                        {requestId && (
                            <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                                <p className="text-xs sm:text-sm text-green-700 mb-1">Request ID:</p>
                                <p className="text-xs sm:text-sm font-mono text-green-800 break-all">{requestId}</p>
                            </div>
                        )}

                        <div className="bg-green-50 border border-green-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
                            <h3 className="font-semibold text-green-800 mb-3 sm:mb-4 text-sm sm:text-base lg:text-lg">What happens next?</h3>
                            <ul className="text-green-700 text-xs sm:text-sm space-y-1.5 sm:space-y-2 text-left">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">✓</span>
                                    <span className="leading-relaxed">Your account deletion request is now confirmed</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">✓</span>
                                    <span className="leading-relaxed">Our team will review your request within 24-48 hours</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">✓</span>
                                    <span className="leading-relaxed">You'll receive an email once the process is complete</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">✓</span>
                                    <span className="leading-relaxed">All your data will be permanently deleted</span>
                                </li>
                            </ul>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                            <button
                                onClick={() => navigate(routes.login)}
                                className="w-full bg-primary hover:bg-primary-dark text-white py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 text-sm sm:text-base lg:text-lg"
                            >
                                Back to Login
                            </button>

                            {/* <button
                                onClick={() => navigate('/account-deletion/status')}
                                className="w-full bg-gray-500 hover:bg-gray-600 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                                Check Status
                            </button> */}
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 10 }}
                            className="bg-red-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6"
                        >
                            <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-2xl sm:text-3xl" />
                        </motion.div>

                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600 mb-2 sm:mb-3">Verification Failed</h1>
                        <p className="text-body text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">{message}</p>

                        {/* Error Details */}
                        <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
                            <h3 className="font-semibold text-red-800 mb-2 sm:mb-3 text-sm sm:text-base">Possible reasons:</h3>
                            <ul className="text-red-700 text-xs sm:text-sm space-y-1.5 sm:space-y-2 text-left">
                                <li className="flex items-start gap-2">
                                    <span className="text-red-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">•</span>
                                    <span className="leading-relaxed">Link has expired (valid for 24 hours)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">•</span>
                                    <span className="leading-relaxed">Link has already been used</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">•</span>
                                    <span className="leading-relaxed">Invalid or corrupted link</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-500 mt-0.5 sm:mt-1 text-xs sm:text-sm">•</span>
                                    <span className="leading-relaxed">Request was cancelled or deleted</span>
                                </li>
                            </ul>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                            <button
                                onClick={() => navigate(routes.login)}
                                className="w-full bg-primary hover:bg-primary-dark text-white py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 text-sm sm:text-base lg:text-lg"
                            >
                                Back to Login
                            </button>

                            {/* <button
                                onClick={() => navigate('/account-deletion')}
                                className="w-full bg-gray-500 hover:bg-gray-600 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                Request Deletion Again
                            </button> */}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gradientStart to-gradientEnd flex items-center justify-center p-2 sm:p-4">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full blur-xl"></div>
                <div className="absolute bottom-20 right-20 w-40 h-40 bg-white rounded-full blur-xl"></div>
                <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white rounded-full blur-xl"></div>
            </div>

            <motion.div
                className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-lg w-full mx-2 sm:mx-4 border border-white/20 relative z-10 max-h-[90vh] overflow-y-auto"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                {renderContent()}

                {/* Footer */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 text-center">
                    <p className="text-xs text-gray-500">
                        Powered by <span className="font-semibold text-primary">PawDash</span>
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default AccountDeletionConfirmation;
